import { Button, DatePicker, Form, Input, Modal, Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { api, showErrorNotification, showSuccessNotification } from '@med-fe/util';
import moment, { Moment } from 'moment';
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import GenericGrid from '../grid/GenericGrid/GenericGrid';

interface BulkTempScheduleRow {
  id: string;
  OfficeNumber: string | number;
  OfficeName: string;
  DayOfWeek: string;
  Date: string;
  OpenTime: string;
  CloseTime: string;
}

const columns = [
  { accessorKey: 'OfficeNumber', header: 'OFFICE NUMBER', size: 150, filterFn: 'contains' },
  { accessorKey: 'OfficeName', header: 'OFFICE NAME', size: 200, filterFn: 'contains' },
  { accessorKey: 'DayOfWeek', header: 'DAY OF WEEK', size: 150, filterFn: 'contains' },
  {
    accessorKey: 'Date',
    header: 'DATE',
    size: 130,
    filterFn: 'contains',
    Cell: ({ cell }: any) => formatDate(cell.getValue()),
  },
  { accessorKey: 'OpenTime', header: 'OPEN TIME', size: 130, filterFn: 'contains' },
  { accessorKey: 'CloseTime', header: 'CLOSE TIME', size: 130, filterFn: 'contains' },
];

function formatDate(value: string) {
  if (!value) {
    return '';
  }

  const date = moment(value);
  return date.isValid() ? date.format('YYYY-MM-DD') : value;
}

function getTimeMinutes(value: string) {
  const parsed = moment(value.trim(), ['h:mm A', 'h A', 'HH:mm:ss', 'HH:mm', 'H'], true);
  return parsed.isValid() ? parsed.hours() * 60 + parsed.minutes() : null;
}

function BulkTempScheduleOverride() {
  const today = moment().startOf('day');
  const [fromDate, setFromDate] = useState<Moment | null>(() => moment().startOf('day'));
  const [toDate, setToDate] = useState<Moment | null>(() => moment().startOf('day'));
  const [officeFilter, setOfficeFilter] = useState('');
  const [openBefore, setOpenBefore] = useState('');
  const [closeAfter, setCloseAfter] = useState('');
  const [rows, setRows] = useState<BulkTempScheduleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processConfirmationOpen, setProcessConfirmationOpen] = useState(false);
  const busy = loading || processing;
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const selectedRows = rows.filter((row) => rowSelection[row.id]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [deleteButtonContainer, setDeleteButtonContainer] = useState<HTMLDivElement | null>(null);

  const gridColumns = [
    ...columns,
    {
      accessorKey: 'Actions',
      header: 'ACTIONS',
      size: 90,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }: any) => (
        <Tooltip title='Delete row'>
          <Button
            type='text'
            danger
            icon={<DeleteOutlined />}
            aria-label='Delete row'
            disabled={busy || processConfirmationOpen}
            onClick={() => setPendingDeleteIds([row.original.id])}
          />
        </Tooltip>
      ),
    },
  ];

  const confirmDelete = () => {
    const deletedIds = new Set(pendingDeleteIds);
    setRows((currentRows) => currentRows.filter((row) => !deletedIds.has(row.id)));
    setRowSelection((currentSelection) => {
      const nextSelection = { ...currentSelection };
      deletedIds.forEach((id) => delete nextSelection[id]);
      return nextSelection;
    });
    setPendingDeleteIds([]);
  };

  const filteredRows = useMemo(() => {
    const officeQuery = officeFilter.trim().toLowerCase();
    const openBeforeMinutes = openBefore ? timeInputToMinutes(openBefore) : null;
    const closeAfterMinutes = closeAfter ? timeInputToMinutes(closeAfter) : null;

    return rows.filter((row) => {
      const matchesOffice =
        !officeQuery ||
        String(row.OfficeNumber).toLowerCase().includes(officeQuery) ||
        row.OfficeName.toLowerCase().includes(officeQuery);
      const openingMinutes = getTimeMinutes(row.OpenTime);
      const closingMinutes = getTimeMinutes(row.CloseTime);
      const matchesOpeningTime =
        openBeforeMinutes === null || (openingMinutes !== null && openingMinutes < openBeforeMinutes);
      const matchesClosingTime =
        closeAfterMinutes === null || (closingMinutes !== null && closingMinutes > closeAfterMinutes);

      return matchesOffice && matchesOpeningTime && matchesClosingTime;
    });
  }, [closeAfter, officeFilter, openBefore, rows]);

  const isDateRangeValid =
    fromDate !== null &&
    toDate !== null &&
    !fromDate.isAfter(toDate, 'day') &&
    !fromDate.isBefore(today, 'day') &&
    !toDate.isBefore(today, 'day');

  const loadOfficeOutOfHours = async () => {
    if (!isDateRangeValid || !fromDate || !toDate || busy || processConfirmationOpen) {
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        fromDate: fromDate.format('YYYY-MM-DD'),
        toDate: toDate.format('YYYY-MM-DD'),
      });
      const response = await api.get(`/schedules/office_out_of_hours?${params.toString()}`);
      const records = Array.isArray(response) ? response : response?.data;
      if (!Array.isArray(records)) {
        throw new Error('Invalid office out of hours response');
      }

      const loadedRows: BulkTempScheduleRow[] = records.map((record, index) => ({
        id: String(index),
        OfficeNumber: record.officeNum ?? record.OfficeNumber ?? record.officeNumber ?? '',
        OfficeName: record.OfficeName ?? record.officeName ?? '',
        DayOfWeek: String(record.DayOfWeek ?? record.dayOfWeek ?? record.dayofweek ?? ''),
        Date: record.Date ?? record.date ?? '',
        OpenTime: record.officeOpenTime ?? record.OfficeOpenTime ?? record.OpenTime ?? record.openTime ?? '',
        CloseTime: record.officeCloseTime ?? record.OfficeCloseTime ?? record.CloseTime ?? record.closeTime ?? '',
      }));
      setRows(loadedRows);
      setRowSelection(
        loadedRows.reduce<Record<string, boolean>>((selection, row) => {
          selection[row.id] = true;
          return selection;
        }, {})
      );
    } catch {
      showErrorNotification('Unable to load office out of hours. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const processSchedules = async () => {
    if (busy || selectedRows.length === 0) {
      return;
    }

    setProcessing(true);
    try {
      const payload = selectedRows.map((row) => {
        const date = moment(row.Date);
        const openTime = moment(row.OpenTime.trim(), ['h:mm A', 'h A', 'HH:mm:ss', 'HH:mm', 'H'], true);
        const closeTime = moment(row.CloseTime.trim(), ['h:mm A', 'h A', 'HH:mm:ss', 'HH:mm', 'H'], true);
        if (!date.isValid() || !openTime.isValid() || !closeTime.isValid()) {
          throw new Error(`Invalid date or time for office ${row.OfficeNumber}. Please reload the schedule data.`);
        }

        return {
          OfficeNumber: String(row.OfficeNumber),
          DayOfWeek: row.DayOfWeek,
          Date: date.format('YYYY-MM-DD'),
          OpenTime: openTime.format('HH:mm:ss'),
          CloseTime: closeTime.format('HH:mm:ss'),
        };
      });
      const response = await api.post('/schedule-change-requests/bulk-temporary', payload, null, true, true);
      showSuccessNotification(`Successfully applied ${response.applied} temporary schedule row(s).`);
      const processedIds = new Set(selectedRows.map((row) => row.id));
      setRows((currentRows) => currentRows.filter((row) => !processedIds.has(row.id)));
      setRowSelection({});
      setProcessConfirmationOpen(false);
    } catch (error: any) {
      const messages = error?.response?.data?.error?.messages || error?.response?.data?.errors;
      showErrorNotification(
        Array.isArray(messages)
          ? messages.join('\n')
          : error?.message || 'Unable to process temporary schedules. Please try again.'
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      <Form
        layout='vertical'
        className='search-container location-search'
        style={{ flexWrap: 'wrap' }}
        onFinish={loadOfficeOutOfHours}
      >
        <Form.Item label='From Date' className='label-text input-field'>
          <DatePicker
            value={fromDate}
            format='YYYY-MM-DD'
            disabledDate={(date) =>
              date.isBefore(moment().startOf('day'), 'day') || Boolean(toDate && date.isAfter(toDate, 'day'))
            }
            onChange={setFromDate}
            inputReadOnly
          />
        </Form.Item>
        <Form.Item label='To Date' className='label-text input-field'>
          <DatePicker
            value={toDate}
            format='YYYY-MM-DD'
            disabledDate={(date) =>
              date.isBefore(moment().startOf('day'), 'day') || Boolean(fromDate && date.isBefore(fromDate, 'day'))
            }
            onChange={setToDate}
            inputReadOnly
          />
        </Form.Item>
        <Form.Item className='search-btn'>
          <Button
            type='primary'
            htmlType='submit'
            disabled={!isDateRangeValid || pendingDeleteIds.length > 0 || processing || processConfirmationOpen}
            loading={loading}
          >
            LOAD W2W DATA
          </Button>
        </Form.Item>
      </Form>
      <Form layout='vertical' className='search-container location-search' style={{ flexWrap: 'wrap' }}>
        <Form.Item label='Office Number or Name' className='label-text' style={{ width: 240, maxWidth: '100%' }}>
          <Input
            allowClear
            placeholder='Office Number or Name'
            value={officeFilter}
            onChange={(event) => setOfficeFilter(event.target.value)}
          />
        </Form.Item>
        <Form.Item label='Opening Before' className='label-text input-field'>
          <Input type='time' value={openBefore} onChange={(event) => setOpenBefore(event.target.value)} />
        </Form.Item>
        <Form.Item label='Closing After' className='label-text input-field'>
          <Input type='time' value={closeAfter} onChange={(event) => setCloseAfter(event.target.value)} />
        </Form.Item>
        <Form.Item className='search-btn'>
          <Button
            onClick={() => {
              setOfficeFilter('');
              setOpenBefore('');
              setCloseAfter('');
            }}
          >
            RESET
          </Button>
        </Form.Item>
        <Form.Item className='search-btn'>
          <div ref={setDeleteButtonContainer} />
        </Form.Item>
        {rows.length > 0 && (
          <Form.Item className='search-btn'>
            <Button
              type='primary'
              disabled={busy || pendingDeleteIds.length > 0 || selectedRows.length === 0}
              loading={processing}
              onClick={() => setProcessConfirmationOpen(true)}
            >
              Process
            </Button>
          </Form.Item>
        )}
      </Form>
      <GenericGrid
        columns={gridColumns}
        columnOrder={['mrt-row-select', ...gridColumns.map((column) => column.accessorKey)]}
        data={filteredRows}
        isLoading={busy}
        pagination={{ pageIndex: 0, pageSize: 10 }}
        sorting={[{ id: 'OfficeNumber', desc: false }]}
        enableRowSelection={true}
        selectAllMode='all'
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        renderToolbarAlertBannerContent={({ table }) => (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16, padding: '8px 16px' }}>
            <span style={{ fontSize: 11, fontFamily: 'inherit' }}>
              {table.getFilteredSelectedRowModel().rows.length} of {table.getCoreRowModel().rows.length} rows selected
            </span>
            <Button
              size='small'
              style={{ height: 24, padding: '0 10px', fontSize: 11 }}
              disabled={busy}
              onClick={() => table.setRowSelection({})}
            >
              CLEAR SELECTION
            </Button>
          </div>
        )}
        renderTopToolbarCustomActions={({ table }) => {
          const selectedIds = table.getFilteredSelectedRowModel().rows.map((row) => row.original.id);
          return (
            <div>
              {deleteButtonContainer
                ? createPortal(
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      disabled={busy || processConfirmationOpen || selectedIds.length === 0}
                      onClick={() => setPendingDeleteIds(selectedIds)}
                    >
                      DELETE SELECTED ({selectedIds.length})
                    </Button>,
                    deleteButtonContainer
                  )
                : null}
            </div>
          );
        }}
        columnPinning={{}}
        filterDisplayMode='popover'
        highlightFilterMatch={true}
        paginationDisplayMode='pages'
        paginationProps={{
          color: 'primary',
          shape: 'rounded',
          showRowsPerPage: true,
          variant: 'outlined',
          rowsPerPageOptions: [10, 50, 100, 500, 1000],
        }}
        enableStickyHeader={true}
        enableStickyFooter={true}
        enableColumnPinning={true}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '11px',
            fontFamily: 'inherit',
            textAlign: 'left',
            padding: '6px 10px',
            height: '40px',
            lineHeight: '1.2',
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            fontSize: '11px',
            fontFamily: 'inherit',
            fontWeight: 700,
            textAlign: 'center',
            padding: '6px 10px',
            height: '40px',
            lineHeight: '1.2',
          },
        }}
        muiTableBodyRowProps={{
          sx: {
            height: '40px',
            '& td': { padding: '6px 10px', height: '40px' },
          },
        }}
        totalRecordsLabel='TOTAL OFFICES'
      />
      <Modal
        title='Process Temporary Schedule Confirmation'
        open={processConfirmationOpen}
        centered
        okText='Ok'
        cancelText='Cancel'
        confirmLoading={processing}
        okButtonProps={{ disabled: selectedRows.length === 0 }}
        cancelButtonProps={{ disabled: processing }}
        closable={!processing}
        maskClosable={!processing}
        keyboard={!processing}
        onOk={processSchedules}
        onCancel={() => !processing && setProcessConfirmationOpen(false)}
      >
        Are you sure you want to process {selectedRows.length} selected temporary schedule row(s)?
      </Modal>
      <Modal
        title='Delete Schedule Confirmation'
        open={pendingDeleteIds.length > 0}
        centered
        okText='Delete'
        cancelText='Cancel'
        okButtonProps={{ danger: true }}
        onOk={confirmDelete}
        onCancel={() => setPendingDeleteIds([])}
      >
        Are you sure you want to remove{' '}
        {pendingDeleteIds.length === 1 ? 'this row' : `these ${pendingDeleteIds.length} rows`} from the grid?
      </Modal>
    </div>
  );
}

function timeInputToMinutes(value: string) {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
}

export default BulkTempScheduleOverride;
